import { useMemo } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { filter, groupBy, keyBy, map, orderBy, uniqBy } from 'lodash-es'
import { PencilIcon, PlusIcon } from 'lucide-react'
import { z } from 'zod'

import { cn } from '@/lib/utils.ts'
import { Button } from '@/components/ui/button.tsx'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { Breadcrumbs } from '@/components/layout/breadcrumbs.tsx'
import { PageTitle } from '@/components/layout/page-title.tsx'
import { QuerySpinner } from '@/components/spinner/query-spinner.tsx'
import { AttributeGroup } from '@/features/attribute-groups/types.ts'
import { useFindAttributes } from '@/features/attributes/hooks/use-find-attributes.ts'
import { Attribute } from '@/features/attributes/types.ts'

export const Route = createFileRoute('/_authenticated/settings/attributes/')({
  component: AttributesListPage,
  validateSearch: z
    .object({
      isGrouped: z.coerce.boolean().default(true),
    })
    .optional(),
})

function AttributesListPage() {
  const findAttributesQuery = useFindAttributes()
  const attributes = findAttributesQuery.data?.data
  const { isGrouped } = Route.useSearch() ?? {}

  const attributesByGroup = useMemo(() => {
    const groupedLinks =
      attributes?.flatMap((a) =>
        (a.attributes_on_group ?? []).map((link) => ({
          attribute: a,
          groupId: link.attribute_group_id,
          group: link.attribute_group,
        }))
      ) ?? []

    const ungroupedAttributes =
      attributes?.filter(
        (a) => !a.attributes_on_group || a.attributes_on_group.length === 0
      ) ?? []

    // Create a dictionary of AttributeGroup by ID
    const attributeGroupsDict = keyBy(
      uniqBy(groupedLinks, (link) => link.groupId).map((link) => link.group),
      (group) => group?.id
    )

    // Group attributes by groupId
    const groupedAttributes = groupBy(groupedLinks, (link) => link.groupId)

    // Convert to [group, attributes[]] format
    const result = map(groupedAttributes, (links, groupId) => {
      const group = attributeGroupsDict[groupId]
      const attributes = uniqBy(
        links.map((link) => link.attribute),
        'id'
      )
      return [group, attributes] as [AttributeGroup, Attribute[]]
    })

    // Add a special group for ungrouped attributes
    if (ungroupedAttributes.length > 0) {
      result.push([
        {
          id: 'ungrouped',
          name: 'Senza Gruppo',
        } as AttributeGroup,
        ungroupedAttributes,
      ])
    }

    return orderBy(result, ([group]) => group?.name, ['asc'])
  }, [attributes])

  return (
    <>
      <Breadcrumbs
        items={[
          { name: 'Impostazioni' },
          {
            name: 'Attributi',
            to: '/settings/attributes',
          },
        ]}
      />

      <ScrollArea className="h-full max-w-screen-sm p-4">
        <PageTitle className="flex justify-between">
          Attributi
          <div className="flex items-center gap-x-2">
            <Link
              className="flex items-center space-x-2"
              to="."
              search={(s) => ({ ...s, isGrouped: !s?.isGrouped })}
            >
              <Checkbox checked={isGrouped} />
              <label className="select-none text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Raggruppa
              </label>
            </Link>
            <Link to="/settings/attributes/create">
              <Button variant="outline" size="sm">
                <PlusIcon /> Nuovo
              </Button>
            </Link>
          </div>
        </PageTitle>

        <QuerySpinner query={findAttributesQuery} wrapperClassName="py-10">
          {isGrouped &&
            attributesByGroup.map(([attributeGroup, attributes]) => (
              <Card
                key={attributeGroup?.id}
                className={cn(
                  'mb-10',
                  !attributeGroup?.name && 'border-dashed border-red-500'
                )}
              >
                <CardHeader className="bg-accent">
                  <CardTitle className="sticky top-0 text-lg">
                    {attributeGroup?.name ?? 'Senza gruppo'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/*<h4 className="scroll-m-20 text-xl font-semibold tracking-tight">*/}
                  {/*  {attributeGroup?.name}*/}
                  {/*</h4>*/}

                  <AttributesList
                    attributes={attributes}
                    attributeGroupToHide={attributeGroup?.id}
                  />
                </CardContent>
              </Card>
            ))}

          {!isGrouped && <AttributesList attributes={attributes} />}
        </QuerySpinner>
      </ScrollArea>
    </>
  )
}

function AttributesList({
  attributes: attrs,
  attributeGroupToHide,
}: {
  attributes?: Array<Attribute> | null
  attributeGroupToHide?: string
}) {
  const attributes = useMemo(() => {
    if (attributeGroupToHide) {
      return attrs?.map((a) => ({
        ...a,
        attributes_on_group: filter(
          a.attributes_on_group,
          (ag) => ag.attribute_group_id !== attributeGroupToHide
        ),
      }))
    }
    return attrs
  }, [attributeGroupToHide, attrs])

  return (
    <ul role="list" className="divide-y divide-gray-100">
      {attributes?.map((attribute) => (
        <li
          key={attribute.id}
          className="flex items-center justify-between gap-x-6 py-5"
        >
          <div className="flex min-w-0 gap-x-4">
            <div className="min-w-0 flex-auto">
              <p className="text-sm/6 font-semibold text-gray-900">
                {attribute.name}
              </p>
              <p className="mt-1 truncate text-xs/5 text-gray-500">
                <b>Tipo</b>: {attribute.json_schema?.type}
              </p>
              <p className="mt-1 line-clamp-1 text-xs/5 text-gray-500">
                <b>Descrizione</b>: {attribute.json_schema?.description}
              </p>
              {attribute.attributes_on_group?.length > 0 && (
                <p className="mt-1 text-xs/5 text-gray-500">
                  <b>
                    Presente {attributeGroupToHide ? 'anche ' : null}
                    in ({attribute.attributes_on_group?.length}):
                  </b>{' '}
                  {attribute.attributes_on_group
                    ?.map(({ attribute_group }) => attribute_group.name)
                    .join(', ')}
                </p>
              )}
            </div>
          </div>
          <div>
            <Link
              to="/settings/attributes/edit/$attributeId"
              params={{ attributeId: attribute.id }}
            >
              <Button variant="outline" size="icon">
                <PencilIcon />
              </Button>
            </Link>
          </div>
        </li>
      ))}
    </ul>
  )
}
